import { CheckoutForm } from "features/settings/Subscription/styles.ts";
import Form from "common/Form/Form.tsx";
import Text from "common/Typography/Text.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import Button from "common/Buttons/Button.tsx";
import React, { ReactNode, useContext, useState } from "react";
import { FormInstance } from "antd/es/form";
import Flex from "common/Boxes/Flex.tsx";
import LinkButton from "common/Buttons/LinkButton.tsx";
import { EMAIL_FORM_ITEM_RULES } from "features/auth/constants.ts";
import Input from "antd/es/input";
import { useUser } from "@shared/utils/hooks/misc.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import { DURATION_TO_PRIMARY_DISPLAY } from "features/settings/common/types.ts";
import CornixTerms, {
  StyledCheckbox,
} from "features/settings/Subscription/CornixTerms.tsx";
import { ASSET_MANAGER_TERMS_OF_USE_LINK } from "@shared/common/constants.ts";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc.ts";

interface PaypalCheckoutProps {
  form: FormInstance;
  setEmail: (email?: string) => void;
  getPaymentLink: () => void;
  loadingPaymentLink?: boolean;
  expirationText?: ReactNode;
  promoCodeRow?: ReactNode;
  buyApiSlotsContent?: ReactNode;
  disabled?: boolean;
}

const PaypalCheckout = ({
  form,
  setEmail,
  getPaymentLink,
  loadingPaymentLink,
  expirationText,
  promoCodeRow,
  buyApiSlotsContent,
  disabled,
}: PaypalCheckoutProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const { user } = useUser();
  const { isAutoRenewal, plans, duration, plan } = useContext(
    BuySubscriptionContext
  )!;
  const currentPlan = user
    ? plans?.[user.subscription_plan_type]?.[duration]
    : undefined;
  const termsLink = isWhitelabelSubscriptionType(plan?.subscription_plan_type)
    ? ASSET_MANAGER_TERMS_OF_USE_LINK
    : undefined;
  const durationDisplay =
    currentPlan &&
    t(DURATION_TO_PRIMARY_DISPLAY[currentPlan.subscription_days]);
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>(
    user?.email ? CheckoutStep.CHECKOUT : CheckoutStep.EMAIL
  );
  const isEmailStep = checkoutStep === CheckoutStep.EMAIL;
  const isCheckoutStep = checkoutStep === CheckoutStep.CHECKOUT;

  const onPaypalFinish = () => {
    if (isEmailStep) {
      setCheckoutStep(CheckoutStep.CHECKOUT);
    } else {
      getPaymentLink();
    }
  };

  const emailFormItem = (
    <Form.Item
      name="email"
      label={
        <Flex style={{ width: "100%", marginBottom: -4 }}>
          {t("paypal_checkout.email")}
          {!isEmailStep && (
            <LinkButton
              type="link"
              onClick={() => setCheckoutStep(CheckoutStep.EMAIL)}
              style={{ paddingRight: 0 }}
            >
              {t("paypal_checkout.update")}
            </LinkButton>
          )}
        </Flex>
      }
      rules={EMAIL_FORM_ITEM_RULES}
      style={{ marginBottom: 10 }}
    >
      <Input
        onChange={(e) => setEmail(e.target.value)}
        disabled={isCheckoutStep}
      />
    </Form.Item>
  );

  const renewalAgreement = isAutoRenewal && (
    <>
      <Form.Item
        style={{ marginTop: 16, marginBottom: 12 }}
        name="renewalAgreement"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) =>
              value
                ? Promise.resolve()
                : Promise.reject(t("paypal_checkout.agreement_error")),
          },
        ]}
      >
        <StyledCheckbox>
          <Text $size={typography.fontSizes.S} style={{ marginLeft: 4 }}>
            {t("paypal_checkout.agreement_text", {
              duration: durationDisplay?.toLowerCase(),
            })}
          </Text>
        </StyledCheckbox>
      </Form.Item>
      <CornixTerms termsLink={termsLink} />
    </>
  );

  const getSubscriptionContent = () => {
    return (
      <>
        {expirationText}
        {renewalAgreement}
        {promoCodeRow}
      </>
    );
  };

  const getApiSlotsContent = () => {
    return (
      <>
        {buyApiSlotsContent}
        {renewalAgreement}
      </>
    );
  };

  const getCheckoutContent = () => {
    return (
      <>
        {!user?.email && (
          <div style={{ marginBottom: 16 }}>{emailFormItem}</div>
        )}
        {buyApiSlotsContent ? getApiSlotsContent() : getSubscriptionContent()}
        <Button
          type="primary"
          htmlType="submit"
          style={{ width: "100%", marginTop: 16 }}
          loading={loadingPaymentLink}
          disabled={disabled}
        >
          {t("paypal_checkout.pay_now")}
        </Button>
      </>
    );
  };

  const getEmailContent = () => {
    return (
      <>
        {emailFormItem}
        <Button
          type="primary"
          htmlType="submit"
          style={{ width: "100%", marginTop: 6 }}
        >
          {t("paypal_checkout.continue")}
        </Button>
      </>
    );
  };

  return (
    <CheckoutForm
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={onPaypalFinish}
    >
      {isEmailStep ? getEmailContent() : getCheckoutContent()}
    </CheckoutForm>
  );
};

export default PaypalCheckout;

enum CheckoutStep {
  EMAIL = "email",
  CHECKOUT = "checkout",
}
